import { useEffect, useRef, useState } from 'react';
import { Tooltip } from '@carbon/react';
import { ArrowsHorizontal, Draggable, SubtractAlt } from '@carbon/react/icons';
import { format0 } from 'utils/number';
import type { SectionWellRow } from './types';
import styles from './cross-section-settings-panel.module.scss';

export interface SectionWellsListProps {
  /** The traverse, in the order the plane runs. */
  wells: SectionWellRow[];
  focusedWellUwi: string | null;
  onFocus: (uwi: string) => void;
  onRemove: (well: SectionWellRow) => void;
  onReplace: (well: SectionWellRow, position: number) => void;
  /**
   * The list after a hand edit, in the order it now reads. The panel commits it as the
   * new as-picked order — see `sectionOrderingAtom`.
   */
  onReorder: (wells: SectionWellRow[]) => void;
}

/**
 * Moves one entry of a list to another index.
 *
 * @param {SectionWellRow[]} wells - The list.
 * @param {number} from - The index moving.
 * @param {number} to - Where it lands.
 * @return {SectionWellRow[]} The reordered list.
 */
const move = (
  wells: SectionWellRow[],
  from: number,
  to: number
): SectionWellRow[] => {
  const next = [...wells];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
};

/**
 * The wells on the section, in traverse order, and the ways to change it.
 *
 * The position on each row is the key the plot's guide and the map's numbered dot
 * share, so one well can be followed across all three; reordering therefore reorders
 * the section itself, not just this list.
 *
 * Reordering works by dragging and from the keyboard both, because a grip that only
 * answers to a mouse makes the section's order unreachable without one. Each row's grip
 * takes arrow keys, and keeps focus as the row moves so a well can be walked several
 * places in one go.
 *
 * @param {SectionWellsListProps} props - Component props.
 * @return {JSX.Element} The list.
 */
export const SectionWellsList = ({
  wells,
  focusedWellUwi,
  onFocus,
  onRemove,
  onReplace,
  onReorder,
}: SectionWellsListProps): JSX.Element => {
  const dragIndex = useRef<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  // Picking a well on the plot highlights its row, and the grip has to be re-found
  // after a keyboard move so the next arrow keeps moving the same well.
  const gripRefs = useRef(new Map<string, HTMLButtonElement | null>());
  // Picking a well on the plot highlights its row, which is no use if the row is
  // scrolled out of the list.
  const rowRefs = useRef(new Map<string, HTMLButtonElement | null>());
  const [announcement, setAnnouncement] = useState('');

  useEffect(() => {
    if (focusedWellUwi === null) {
      return;
    }
    rowRefs.current.get(focusedWellUwi)?.scrollIntoView({ block: 'nearest' });
  }, [focusedWellUwi]);

  const reorder = (from: number, to: number) => {
    if (to < 0 || to >= wells.length || from === to) {
      return;
    }
    const well = wells[from];
    onReorder(move(wells, from, to));
    setAnnouncement(
      `${well.name ?? well.uwi} moved to position ${format0(
        to + 1
      )} of ${format0(wells.length)}.`
    );
    // The row has moved under the cursor; the grip that was being used should still be
    // the one holding focus.
    window.requestAnimationFrame(() => {
      gripRefs.current.get(well.uwi)?.focus();
    });
  };

  const onGripKeyDown = (event: React.KeyboardEvent, index: number) => {
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      reorder(index, index - 1);
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      reorder(index, index + 1);
      return;
    }
    if (event.key === 'Home') {
      event.preventDefault();
      reorder(index, 0);
      return;
    }
    if (event.key === 'End') {
      event.preventDefault();
      reorder(index, wells.length - 1);
    }
  };

  return (
    <>
      <ol className={styles.wellList} aria-label="Wells on the section">
        {wells.map((well, index) => (
          <li
            key={well.uwi}
            className={
              dragOverIndex === index
                ? styles.wellRowItemDragOver
                : styles.wellRowItem
            }
            onDragOver={(event) => {
              event.preventDefault();
              setDragOverIndex(index);
            }}
            onDrop={() => {
              const from = dragIndex.current;
              setDragOverIndex(null);
              dragIndex.current = null;
              if (from !== null) {
                reorder(from, index);
              }
            }}
          >
            <Tooltip
              align="right"
              label={`Reorder ${
                well.name ?? well.uwi
              } — drag, or use the arrow keys`}
            >
              <button
                type="button"
                ref={(node) => gripRefs.current.set(well.uwi, node)}
                className={styles.wellGrip}
                draggable
                onDragStart={() => {
                  dragIndex.current = index;
                }}
                onDragEnd={() => {
                  dragIndex.current = null;
                  setDragOverIndex(null);
                }}
                onKeyDown={(event) => onGripKeyDown(event, index)}
              >
                <Draggable size={16} />
              </button>
            </Tooltip>
            <button
              type="button"
              ref={(node) => rowRefs.current.set(well.uwi, node)}
              className={
                well.uwi === focusedWellUwi
                  ? styles.wellRowFocused
                  : styles.wellRow
              }
              aria-pressed={well.uwi === focusedWellUwi}
              onClick={() => onFocus(well.uwi)}
            >
              <span className={styles.wellPosition}>{index + 1}</span>
              <span className={styles.wellText}>
                <span className={styles.wellName}>
                  {well.name ?? 'Unnamed well'}
                </span>
                <span className={styles.wellUwi}>
                  {well.totalDepthFt === null
                    ? well.uwi
                    : `${well.uwi} · TD ${format0(well.totalDepthFt)} ft`}
                </span>
              </span>
            </button>
            {/* Swapping one well for another is a single act, not a remove and an
                add: the replacement keeps the position, so the rest of the traverse
                stays where it was. */}
            <Tooltip align="left" label={`Replace ${well.name ?? well.uwi}`}>
              <button
                type="button"
                className={styles.wellAction}
                onClick={() => onReplace(well, index + 1)}
              >
                <ArrowsHorizontal size={16} />
              </button>
            </Tooltip>
            {/* The map takes a well off the section by clicking it again, which only
                works while it is in view — the row is the way out for one that is
                not. */}
            <Tooltip align="left" label={`Remove ${well.name ?? well.uwi}`}>
              <button
                type="button"
                className={styles.wellRemove}
                onClick={() => onRemove(well)}
              >
                <SubtractAlt size={16} />
              </button>
            </Tooltip>
          </li>
        ))}
      </ol>
      <span aria-live="polite" className={styles.visuallyHidden}>
        {announcement}
      </span>
    </>
  );
};
